import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  createElementPdf,
  printWithBodyClass,
} from '../common/printableDocument'
import PdfActions from '../common/PdfActions'
import { getOrderById, getPublishedOrderBooks } from '../services/order_service'
import './DeliveryNote.css'

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback

const getOrderId = (order) =>
  getValue(order, ['orderid', 'orderId', 'order_id', 'id'])
const getCustomerId = (order) =>
  getValue(order, ['customerid', 'customer_id', 'customerId', 'cust_id'], '')
const getBookTitle = (book) =>
  getValue(book, ['book_title', 'bookTitle', 'description', 'title', 'name'])
const getQuantity = (book) =>
  Number(getValue(book, ['quantity', 'ordered_quantity', 'qty'], 0))

const printDeliveryNote = () => printWithBodyClass('print-delivery-note')

const formatDisplayDate = (value) => {
  const date = value ? new Date(value) : new Date()
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
}

function DeliveryNote() {
  const { orderId } = useParams()
  const [searchParams] = useSearchParams()
  const [order, setOrder] = useState(null)
  const [books, setBooks] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloadError, setDownloadError] = useState('')
  const [pdfBlob, setPdfBlob] = useState(null)
  const deliveryNoteSheetRef = useRef(null)
  const downloadStartedRef = useRef(false)
  const isPdfDownload = searchParams.get('download') === 'pdf'

  useEffect(() => {
    let isMounted = true
    const loadOrder = async () => {
      setIsLoading(true)
      setError('')
      try {
        const orderResponse = await getOrderById(orderId)
        if (!orderResponse) {
          throw new Error(`Order ${orderId} could not be found.`)
        }
        if (!isMounted) return
        setOrder(orderResponse)

        const embeddedBooks = getValue(
          orderResponse,
          ['orderBooks', 'order_books', 'books', 'items'],
          null,
        )
        if (Array.isArray(embeddedBooks)) {
          setBooks(embeddedBooks)
        } else if (getCustomerId(orderResponse)) {
          setBooks(await getPublishedOrderBooks(getCustomerId(orderResponse)))
        }
      } catch (requestError) {
        if (isMounted) setError(requestError.message)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }
    loadOrder()
    return () => {
      isMounted = false
    }
  }, [orderId])

  useEffect(() => {
    if (!order || !isPdfDownload || downloadStartedRef.current) return

    downloadStartedRef.current = true
    createElementPdf(deliveryNoteSheetRef.current)
      .then(setPdfBlob)
      .catch((downloadRequestError) => {
        downloadStartedRef.current = false
        setDownloadError(
          downloadRequestError.message || 'The PDF could not be downloaded.',
        )
      })
  }, [isPdfDownload, order, orderId])

  if (isLoading) {
    return (
      <div className='delivery-note-page delivery-note-page--empty'>
        <p>Loading delivery note...</p>
      </div>
    )
  }
  if (error || !order) {
    return (
      <div className='delivery-note-page delivery-note-page--empty'>
        <p>{error || 'Delivery note could not be loaded.'}</p>
      </div>
    )
  }

  return (
    <div className='delivery-note-page'>
      <div className='delivery-note-sheet' ref={deliveryNoteSheetRef}>
        <header className='delivery-note-header'>
          <div className='delivery-note-header__brand'>
            <img
              className='delivery-note-header__logo'
              src='/logo1.jpg'
              alt=''
            />
            <h1>Buck Press Limited</h1>
          </div>
          <div className='delivery-note-header__date'>
            Date: <strong>{formatDisplayDate()}</strong>
          </div>
        </header>

        <section className='delivery-note-parties'>
          <div>
            <span className='delivery-note-parties__label'>From</span>
            <strong>Buck Press Limited</strong>
            <p>Accra - Nsawam Highway, Ofankor</p>
            <p>P.O. Box AN 12321, Accra-Ghana</p>
            <p>Tel: (233) 302-408061</p>
            <p>Email: publishing@buckpress.com</p>
          </div>
          <div>
            <span className='delivery-note-parties__label'>To</span>
            <strong>
              {getValue(order, ['cust_name', 'customer_name', 'customer'])}
            </strong>
            <p>{getValue(order, ['address', 'cust_address', 'location'])}</p>
            <p>
              Phone: {getValue(order, ['phone', 'contact', 'cust_contact'])}
            </p>
          </div>
          <div className='delivery-note-parties__meta'>
            <p>
              Invoice #<strong>{getOrderId(order)}</strong>
            </p>
            <p>
              Sales Executive:{' '}
              <strong>
                {getValue(order, [
                  'sales_exec_name',
                  'salesExecutive',
                  'sales_executive',
                  'sales_exec',
                ])}
              </strong>
            </p>
            <p>
              Requesting Officer:{' '}
              <strong>
                {getValue(order, [
                  'requesting_user',
                  'requested_by',
                  'user_fullname',
                  'fullname',
                ])}
              </strong>
            </p>
          </div>
        </section>

        <h2 className='delivery-note-title'>Delivery Note</h2>

        <div className='delivery-note-table-wrap'>
          <table className='delivery-note-table'>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Book Title</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {books.map((book, index) => (
                <tr key={getValue(book, ['bookid', 'book_id', 'id'], index)}>
                  <td>{index + 1}</td>
                  <td>{getBookTitle(book)}</td>
                  <td>{getQuantity(book)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <table className='delivery-note-confirm'>
          <tbody>
            <tr>
              <th>Vehicle No:</th>
              <td />
            </tr>
            <tr>
              <th>Driver's Name:</th>
              <td />
            </tr>
            <tr>
              <th>
                All above goods received.
                <br />
                In good order and condition.
                <br />
                Signature with Company's Rubber Stamp.
              </th>
              <td />
            </tr>
          </tbody>
        </table>

        <div className='delivery-note-actions' data-html2canvas-ignore='true'>
          {isPdfDownload ? (
            downloadError ? (
              <button
                type='button'
                onClick={() => {
                  downloadStartedRef.current = false
                  setDownloadError('')
                  createElementPdf(deliveryNoteSheetRef.current)
                    .then(setPdfBlob)
                    .catch((downloadRequestError) =>
                      setDownloadError(downloadRequestError.message),
                    )
                }}
              >
                Try again
              </button>
            ) : pdfBlob ? (
              <PdfActions
                pdfBlob={pdfBlob}
                filename={`delivery-note-${orderId}.pdf`}
                onPrint={printDeliveryNote}
                onError={setDownloadError}
              />
            ) : (
              <span>Preparing PDF...</span>
            )
          ) : (
            <button type='button' onClick={printDeliveryNote}>
              <span aria-hidden='true'>🖨</span> Print
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeliveryNote
